import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Checkbox,
    FormControlLabel,
    CardActions,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from '../../../components/buttons/customButton';
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { getContactThunk, getTagsThunk, updateContactThunk } from "../../contact/contactThunk";
import { getMyTaskThunk } from "../../leadMangement/leadMangementThunk";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

export default function AssignTagModal({ show, handleClose }) {
    const dispatch = useDispatch();
    const { selectedUser, status } = useSelector((state) => state.messages);
    const { getTags } = useSelector((state) => state.contacts);
    const { waMessageContacts } = useSelector((state) => state.messages);
    const { openLeadChatConvo } = useSelector(
        (state) => state.leadManagement
    );

    const [changedFields, setChangedFields] = useState(new Set());
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [state, setState] = useState({ selectedUserData: [], searchTags: "" });

    const tagDetails = waMessageContacts.find((item) => item.wa_contact_id === selectedUser);

    useEffect(() => {
        let selectedData = [];
        if (show) {
            if (tagDetails?.tag_id != null) {
                selectedData = tagDetails.tag_id.split(",").map(Number);
            }
            setState((prev) => ({ ...prev, searchTags: "", selectedUserData: selectedData }));
        } else {
            setState((prev) => ({ ...prev, searchTags: "", selectedUserData: [] }));
        }
        setChangedFields(new Set());
    }, [show, waMessageContacts, selectedUser, tagDetails]);

    // Filter tags based on search
    const filteredTags = getTags.filter(tag =>
        tag.tag_name.toLowerCase().includes(state.searchTags.toLowerCase())
    );

    const handleSelectedTags = (e, tagId) => {
        const checked = e.target.checked;
        setState((prev) => {
            const selected = checked
                ? [...prev.selectedUserData, tagId]
                : prev.selectedUserData.filter((id) => id !== tagId);
            return { ...prev, selectedUserData: selected };
        });
        setChangedFields((prev) => new Set(prev).add("selectedUserData"));
    };

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setState((prev) => {
            const allIds = filteredTags.map(tag => tag.id);
            const selected = checked
                ? Array.from(new Set([...prev.selectedUserData, ...allIds]))
                : prev.selectedUserData.filter(id => !allIds.includes(id));
            return { ...prev, selectedUserData: selected };
        });
        setChangedFields((prev) => new Set(prev).add("selectedUserData"));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!changedFields || changedFields.size === 0) {
            setSnackbar({ open: true, message: "No changes to update", severity: "info" });
            setTimeout(() => handleClose(), 1000);
            return;
        }

        const payload = {};
        if (changedFields.has("selectedUserData")) {
            payload.tag_id = state.selectedUserData.toString();
        }
        const payload1 = { ...payload, wa_contact_id: selectedUser };
      
        try {
            const result = await dispatch(updateContactThunk({ credentials: payload1 }));
            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({ open: true, message: result.payload.message, severity: "success" });
                dispatch(getTagsThunk({ type: "tags" }));
                if (openLeadChatConvo) {
                    dispatch(getMyTaskThunk({ type: "getTask" }));
                } else {
                    dispatch(getContactThunk({ type: "getContact" }));
                }
                setState(prev => ({ ...prev, searchTags: "" }));
                handleClose();
            } else {
                setSnackbar({ open: true, message: result.payload, severity: "error" });
            }
        } catch {
            setSnackbar({ open: true, message: "Server error. Please try again.", severity: "error" });
        }
    };

    return (
        <Dialog
            open={show}
            onClose={handleClose}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="md"
            keepMounted
            PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#fdfdfd', boxShadow: 6, mt: 4, mx: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' }, alignSelf: 'flex-start' } }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'var(--primary)', color: '#fff', px: { xs: 2, sm: 3 }, py: 1, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                <DialogTitle sx={{ m: 0, p: 0, fontSize: '1.2rem' }}>Update Tag</DialogTitle>
                <IconButton onClick={handleClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
            </Box>

            {/* Content */}
            <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                {/* Search */}
                <Box mb={2}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search tags..."
                        value={state.searchTags}
                        onChange={(e) => setState(prev => ({ ...prev, searchTags: e.target.value }))}
                    />
                </Box>

                {/* Select All */}
                <Box mb={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={filteredTags.every(tag => state.selectedUserData.includes(tag.id)) && filteredTags.length > 0}
                                onChange={handleSelectAll}
                            />
                        }
                        label="Select All"
                    />
                </Box>

                {/* Tags Grid: 3 columns per row, scrollable */}
                <Box sx={{ maxHeight: 300, overflowY: "auto", display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: 1, "& .MuiFormControlLabel-label": { overflowWrap: "anywhere" } }}>
                    {filteredTags.map((item) => {
                        const exist = state.selectedUserData.includes(item.id);
                        return (
                            <FormControlLabel
                                key={item.id}
                                control={
                                    <Checkbox
                                        checked={exist}
                                        onChange={(e) => handleSelectedTags(e, item.id)}
                                    />
                                }
                                label={item.tag_name}
                            />
                        );
                    })}
                </Box>
            </DialogContent>

            {/* Actions */}
            <CardActions sx={{ display: "flex", justifyContent: "flex-end" }}>
                <CustomButton
                    status={status}
                    onClick={handleSubmit}
                    label="Update"
                    loadingLabel="Submitting.."
                />
            </CardActions>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </Dialog>
    );
}
